import * as swcHelpers from '@swc/helpers';
import { HttpLink, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import ApolloLinkTimeout from 'apollo-link-timeout';

// TODO: fix/improve token caching
// This is a hardcoded map of the queries and mutations that require authentication, when this
// map isn't in place, every single graphQL request will also send a request to
// check the session of auth0, this is a naive way to skip over all the graphQL
// queries which we know don't require authentication
const authOperations = new Set([
    'CancelSubscription',
    'ChangeSubscription',
    'CheckCustomerConsents',
    'CreateEyeExam',
    'CreateStoredCardPayment',
    'CreateStoredSepaPaymentMethod',
    'findSubscription',
    'getSuspendSubscription',
    'getBillingAddress',
    'getChangeSubscriptionPreview',
    'getCurrentSubscriptionDetails',
    'getOptIns',
    'getPersonalDetails',
    'getSocialIdentity',
    'getPreferredStore',
    'GetPreferredStore',
    'getPreferredStoreId',
    'GetPreferredStoreId',
    'GetPreferredStoreWithInventory',
    'GetPreferredStoreCode',
    'getRecentOrders',
    'getPrescriptionDownloads',
    'getAppointmentById',
    'getMyAppointments',
    'getMySubscriptions',
    'getOrderDetails',
    'getMyOrderHistory',
    'getSubscriptionDeliveries',
    'getSubscriptionPayments',
    'getSubscriptionPrescription',
    'getSubscriptionPrescriptionProducts',
    'getSubscriptionNotifications',
    'getSubscriptionProductRateplans',
    'getSuspensionResumeDates',
    'previewChangeSubscription',
    'ResumeSubscription',
    'RetrySubscriptionPayment',
    'SaveNewConsent',
    'SuspendSubscription',
    'UpdateBillingAddress',
    'UpdateDeliveryAddress',
    'updateOptIns',
    'updatePersonalDetails',
    'UpdatePersonalDetails',
    'UpdateSubscriptionBillingAddress',
    'UpdateSubscriptionPrescription',
    'storedPaymentMethods',
    'ChangePaymentMethod',
    'completeChallengeFlowStoredPayment',
    'activateSocialAccount',
    'requestSocialLoginMerge',
    'getBookAppointmentUser',
    'MergeShoppinglists',
    'GetMyWishlist',
    'GetMyWishlistId',
    'GetMySubscriptions',
    'GetMySubscriptionDetails',
    'GetPrescriptionsFromOrders',
    'UpdateSubscriptionInterval',
    'UpdateSubscriptionBillingAddress',
    'UpdateSubscriptionDeliveryAddress',
    'PauseSubscription', 
]);
/**
 * This set is for operations that only require the auth token when the customer is logged in. In some specific use case `authOperations` is not sufficient enough.
 * For instance, for the `SubscribeNewsletter` operation, the BE requires the token to do some additional logic for logged-in customers.
 * Instead of having two separate mutations, we created one mutation where both logged-in and logged-out customers subscribe their email address.
 * Via this extension the FE only sends a token when the customer is logged-in.
 */ const operationsWithOptionalToken = new Set([
    'SubscribeNewsletter',
    'PlaceOrder',
    'UpsertShoppingList',
    'getShoppingListById',
    'GetShoppingListCount',
    'AddToShoppinglist',
    'RemoveFromShoppinglist',
    'CartExpressPaymentMethods', 
]);
const authCallback = (options)=>(operation, previousRequest)=>new Promise(function() {
            var _ref = swcHelpers.asyncToGenerator(function*(resolve, reject) {
                // Only continue if the operationName is one that we know requires authentication
                const requiresAuth = operation.operationName && (authOperations.has(operation.operationName) || operationsWithOptionalToken.has(operation.operationName));
                if (!requiresAuth || !options.getToken) {
                    return resolve(previousRequest);
                }
                try {
                    const token = yield options.getToken();
                    resolve({
                        headers: swcHelpers.objectSpread({}, previousRequest.headers, {
                            authorization: `Bearer ${token}`
                        })
                    });
                } catch (e) {
                    if (operation.operationName && operationsWithOptionalToken.has(operation.operationName)) {
                        return resolve(previousRequest);
                    }
                    reject(e);
                }
            });
            return function(resolve, reject) {
                return _ref.apply(this, arguments);
            };
        }())
;
const authLink = (options)=>setContext(authCallback(options))
;
const httpLink = (options)=>new HttpLink(swcHelpers.objectSpread({
        credentials: 'same-origin',
        fetch
    }, options))
;
const errorLink = onError(({ graphQLErrors , networkError  })=>{
    if (graphQLErrors) {
        graphQLErrors.forEach((error)=>console.error(`[GraphQL error]: ${error.message}`)
        );
    }
    if (networkError) {
        console.error('[Network error]:', networkError);
    }
});
const forwardHeaders = (operation, forward)=>{
    const context = operation.getContext();
    const forwardHeaders1 = context.forwardHeaders;
    operation.setContext(({ headers ={}  })=>({
            headers: swcHelpers.objectSpread({}, headers, forwardHeaders1 && forwardHeaders1)
        })
    );
    return forward(operation);
};
const forwardHeadersLink = ()=>new ApolloLink((operation, forward)=>forwardHeaders(operation, forward)
    )
;
const timeoutLink = (timeoutMs, statusCode)=>new ApolloLinkTimeout(timeoutMs, statusCode)
;
// Default set of operations that should be protected with reCAPTCHA
// These are typically mutation operations that could be abused
const DEFAULT_RECAPTCHA_PROTECTED_OPERATIONS = new Set([
    'SubscribeNewsletter'
]);
/**
 * Creates a context setter function for adding reCAPTCHA tokens to GraphQL requests
 */ const recaptchaCallback = (options)=>{
    return function() {
        var _ref = swcHelpers.asyncToGenerator(function*(operation, previousContext) {
            var ref;
            const { operationName  } = operation;
            if (!operationName) {
                return previousContext;
            }
            const protectedOperations = ((ref = options.recaptcha) === null || ref === void 0 ? void 0 : ref.protectedOperations) || DEFAULT_RECAPTCHA_PROTECTED_OPERATIONS;
            const shouldProtect = protectedOperations.has(operationName);
            if (!shouldProtect || !options.getRecaptchaToken) {
                return previousContext;
            }
            try {
                var ref1, ref2;
                // Execute reCAPTCHA to get token
                const action = ((ref1 = options.recaptcha) === null || ref1 === void 0 ? void 0 : ref1.defaultAction) || `graphql_${operationName.toLowerCase()}`;
                const recaptchaToken = yield options.getRecaptchaToken(action);
                if ((ref2 = options.recaptcha) === null || ref2 === void 0 ? void 0 : ref2.enableLogging) {
                    console.log(`[reCAPTCHA] Generated token for operation "${operationName}" with action "${action}"`);
                }
                // Add reCAPTCHA token to headers
                return swcHelpers.objectSpread({}, previousContext, {
                    headers: swcHelpers.objectSpread({}, previousContext.headers, {
                        'X-Recaptcha-Token': recaptchaToken,
                        'X-Recaptcha-Action': action
                    })
                });
            } catch (error) {
                var ref3;
                const errorMessage = error instanceof Error ? error.message : 'Unknown reCAPTCHA error';
                if ((ref3 = options.recaptcha) === null || ref3 === void 0 ? void 0 : ref3.enableLogging) {
                    console.error(`[reCAPTCHA] Failed to generate token for operation "${operationName}":`, errorMessage);
                }
                // Proceed without reCAPTCHA token if it fails
                return previousContext;
            }
        });
        return function(operation, previousContext) {
            return _ref.apply(this, arguments);
        };
    }();
};
/**
 * Creates an Apollo Link that adds reCAPTCHA tokens to protected GraphQL operations
 */ const recaptchaLink = (options)=>{
    // Always return the link, it will check for token getter internally
    return setContext(recaptchaCallback(options));
};

export { authCallback, authLink, errorLink, forwardHeaders, forwardHeadersLink, httpLink, recaptchaCallback, recaptchaLink, timeoutLink };
//# sourceMappingURL=links.js.map
